import React, { useMemo, useState } from "react";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import CloseIcon from "@mui/icons-material/Close";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

import { PACKAGE_SELECTOR_PAGE_SIZE } from "constants/constants";
import { useBasicPackagesInfo } from "store/backend/apiHooks";

import styles from "./PackageSelector.module.scss";

const getPackageLabel = (pkg) =>
  pkg.displayName || pkg.packageName || `Package ${pkg.packageId}`;

const PackageSelector = ({
  selectAll = false,
  selectedIds = [],
  onChange,
  disabled = false,
  error,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const { data, isLoading, isError, error: fetchError, refetch } =
    useBasicPackagesInfo(!disabled);

  const packages = data ?? [];

  const filteredPackages = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) return packages;

    return packages.filter((pkg) => {
      const displayName = (pkg.displayName || "").toLowerCase();
      const packageName = (pkg.packageName || "").toLowerCase();
      const packageId = String(pkg.packageId ?? "");
      return (
        displayName.includes(query) ||
        packageName.includes(query) ||
        packageId.includes(query)
      );
    });
  }, [packages, searchTerm]);

  const totalPages = Math.max(1, Math.ceil(filteredPackages.length / PACKAGE_SELECTOR_PAGE_SIZE));
  const paginatedPackages = filteredPackages.slice(
    (currentPage - 1) * PACKAGE_SELECTOR_PAGE_SIZE,
    currentPage * PACKAGE_SELECTOR_PAGE_SIZE
  );

  const selectedPackages = useMemo(
    () =>
      selectedIds
        .map((id) => packages.find((pkg) => pkg.packageId === id))
        .filter(Boolean),
    [packages, selectedIds]
  );

  const handleApplyModeChange = (mode) => {
    if (disabled) return;
    if (mode === "all") {
      onChange({ selectAll: true, packageIds: [] });
      return;
    }
    onChange({ selectAll: false, packageIds: selectedIds });
  };

  const togglePackage = (packageId) => {
    if (disabled || selectAll) return;

    const nextSelected = selectedIds.includes(packageId)
      ? selectedIds.filter((id) => id !== packageId)
      : [...selectedIds, packageId];

    onChange({ selectAll: false, packageIds: nextSelected });
  };

  const removePackage = (packageId) => {
    if (disabled) return;
    onChange({
      selectAll: false,
      packageIds: selectedIds.filter((id) => id !== packageId),
    });
  };

  const clearAll = () => {
    if (disabled) return;
    onChange({ selectAll: false, packageIds: [] });
  };

  if (isLoading) {
    return <p className={styles.stateText}>Loading packages...</p>;
  }

  if (isError) {
    return (
      <div className={styles.errorState}>
        <p className={styles.stateText}>
          {fetchError?.message || "Failed to load packages."}
        </p>
        <button
          type="button"
          className={styles.retryBtn}
          onClick={() => refetch()}
        >
          <RefreshOutlinedIcon fontSize="small" />
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.applyRow}>
        <span className={styles.applyLabel}>Apply to</span>
        <div className={styles.radioGroup}>
          <label className={styles.radioOption}>
            <input
              type="radio"
              name="packageApplyMode"
              checked={selectAll}
              onChange={() => handleApplyModeChange("all")}
              disabled={disabled}
            />
            <span>All Packages</span>
          </label>
          <label className={styles.radioOption}>
            <input
              type="radio"
              name="packageApplyMode"
              checked={!selectAll}
              onChange={() => handleApplyModeChange("selected")}
              disabled={disabled}
            />
            <span>Selected Packages</span>
          </label>
        </div>
      </div>

      <div className={styles.selectionPanel}>
        {selectAll ? (
          <div className={styles.selectionPanelAll}>
            <div className={styles.infoBanner}>
              <InfoOutlinedIcon fontSize="small" />
              <span>Coupon will be applicable to all packages from OMS.</span>
            </div>
          </div>
        ) : (
          <>
          <div className={styles.toolbar}>
            <div className={styles.searchWrap}>
              <SearchOutlinedIcon className={styles.searchIcon} fontSize="small" />
              <input
                type="text"
                className={styles.searchInput}
                placeholder="Search packages by name or ID"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
                disabled={disabled}
              />
            </div>
            <span className={styles.clearAllSlot}>
              <button
                type="button"
                className={styles.clearAllBtn}
                onClick={clearAll}
                disabled={disabled || selectedIds.length === 0}
                style={{ visibility: selectedIds.length > 0 ? "visible" : "hidden" }}
              >
                Clear All
              </button>
            </span>
          </div>

          <div className={styles.dualPane}>
            <div className={styles.browsePane}>
              <div className={styles.paneHeader}>
                <h3>Browse Packages</h3>
                <span>{filteredPackages.length} available</span>
              </div>
              <div className={styles.tableWrap}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th />
                      <th>Package Name</th>
                      <th>ID</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedPackages.length === 0 ? (
                      <tr>
                        <td colSpan={3} className={styles.emptyCell}>
                          No packages match your search.
                        </td>
                      </tr>
                    ) : (
                      paginatedPackages.map((pkg) => {
                        const isSelected = selectedIds.includes(pkg.packageId);
                        return (
                          <tr
                            key={pkg.packageId}
                            className={isSelected ? styles.rowSelected : ""}
                          >
                            <td>
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => togglePackage(pkg.packageId)}
                                disabled={disabled}
                              />
                            </td>
                            <td>{getPackageLabel(pkg)}</td>
                            <td>{pkg.packageId}</td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
              <div className={styles.pagination}>
                <span>
                  Showing{" "}
                  {filteredPackages.length === 0
                    ? 0
                    : (currentPage - 1) * PACKAGE_SELECTOR_PAGE_SIZE + 1}{" "}
                  to {Math.min(currentPage * PACKAGE_SELECTOR_PAGE_SIZE, filteredPackages.length)}{" "}
                  of {filteredPackages.length} packages
                </span>
                <div className={styles.paginationControls}>
                  <button
                    type="button"
                    disabled={currentPage === 1 || disabled}
                    onClick={() => setCurrentPage((page) => page - 1)}
                  >
                    <ChevronLeftIcon fontSize="small" />
                  </button>
                  <span>{currentPage}</span>
                  <button
                    type="button"
                    disabled={currentPage >= totalPages || disabled}
                    onClick={() => setCurrentPage((page) => page + 1)}
                  >
                    <ChevronRightIcon fontSize="small" />
                  </button>
                </div>
              </div>
            </div>

            <div className={styles.selectedPane}>
              <div className={styles.paneHeader}>
                <h3>Selected Packages ({selectedPackages.length})</h3>
              </div>
              <div className={styles.selectedList}>
                {selectedPackages.length === 0 ? (
                  <p className={styles.stateText}>No packages selected yet.</p>
                ) : (
                  selectedPackages.map((pkg) => (
                    <div key={pkg.packageId} className={styles.selectedItem}>
                      <div>
                        <span className={styles.selectedName}>
                          {getPackageLabel(pkg)}
                        </span>
                        <span className={styles.selectedMeta}>
                          ID: {pkg.packageId}
                        </span>
                      </div>
                      <button
                        type="button"
                        className={styles.selectedRemove}
                        onClick={() => removePackage(pkg.packageId)}
                        disabled={disabled}
                        aria-label={`Remove ${getPackageLabel(pkg)}`}
                      >
                        <CloseIcon fontSize="small" />
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

          <div className={styles.infoBanner}>
            <InfoOutlinedIcon fontSize="small" />
            <span>Coupon will be applicable only to the selected packages.</span>
          </div>
          </>
        )}
      </div>

      {error && <p className={styles.fieldError}>{error}</p>}
    </div>
  );
};

export default PackageSelector;
